import { jsx as _jsx } from "react/jsx-runtime";
/**
 * BFT Routes Context
 *
 * Manages view-only BFT routes displayed on the map.
 * These are separate from the RouteEditorContext which handles editing.
 *
 * Features:
 * - Add routes to map (view only)
 * - Remove routes from map
 * - Pan/zoom to fit route on add
 * - Track multiple viewed routes
 */
import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef, } from 'react';
import { useMap } from './MapContext';
import { useRouteApi } from '../hooks/useRouteApi';
import { parseNois, isDuctFeature, } from '../types/route-editor';
// =============================================================================
// Context
// =============================================================================
const BftRoutesContext = createContext(null);
export const useBftRoutes = () => {
    const context = useContext(BftRoutesContext);
    if (!context) {
        throw new Error('useBftRoutes must be used within BftRoutesProvider');
    }
    return context;
};
export const BftRoutesProvider = ({ children }) => {
    const { getMap, isMapReady } = useMap();
    const api = useRouteApi();
    const [viewedRoutes, setViewedRoutes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // Track layers for cleanup
    const layersRef = useRef(new Map());
    // =========================================================================
    // Add Route
    // =========================================================================
    const addRoute = useCallback(async (bftRef) => {
        // Check if already viewed
        if (viewedRoutes.some(r => r.ourRef === bftRef)) {
            console.log(`[BftRoutes] Route ${bftRef} already on map`);
            return;
        }
        setIsLoading(true);
        try {
            const data = await api.loadRoute(bftRef);
            const { bft, featureCollection } = data;
            // Collect features and coordinates for bounds
            const features = [];
            const allCoords = [];
            let totalDistance = 0;
            featureCollection.features.forEach(feature => {
                if (isDuctFeature(feature)) {
                    if (!feature.nois) {
                        feature.nois = parseNois(feature.properties.ORREFERENCEIDS);
                    }
                    features.push(feature);
                    totalDistance += feature.properties.MEASURED_LENGTH || 0;
                    // Collect coordinates (GeoJSON is [lng, lat], Leaflet needs [lat, lng])
                    if (feature.geometry.type === 'LineString') {
                        feature.geometry.coordinates.forEach(coord => {
                            allCoords.push([coord[1], coord[0]]);
                        });
                    }
                }
                else if (feature.geometry.type === 'Point') {
                    const coord = feature.geometry.coordinates;
                    allCoords.push([coord[1], coord[0]]);
                }
            });
            // Create Leaflet layer
            const map = getMap();
            const L = window.L;
            const CFN = window.CFN;
            if (!map || !L) {
                throw new Error('Map not available');
            }
            const geoJsonData = {
                type: 'FeatureCollection',
                features,
            };
            const layer = L.geoJSON(geoJsonData, {
                style: CFN?.PIAStyle || ((feature) => ({
                    color: '#0066ff',
                    weight: Math.min((feature.properties?.NUMBER_OF_WAYS || 2) + 1, 6),
                })),
                pointToLayer: CFN?.PIAIcons,
                onEachFeature: (feature, leafletLayer) => {
                    if (CFN?.PIALineStyle) {
                        CFN.PIALineStyle(feature, leafletLayer, true);
                    }
                },
            });
            layer.addTo(map);
            layer.bringToFront();
            // Store layer reference
            layersRef.current.set(bftRef, layer);
            // Create viewed route entry
            const viewedRoute = {
                ourRef: bft.our_ref,
                description: bft.description,
                id: String(bft.id),
                cableType: bft.cableType,
                rfs: bft.rfs,
                sectionCount: features.filter(f => f.geometry.type === 'LineString').length,
                totalDistance,
                layer,
            };
            setViewedRoutes(prev => [...prev, viewedRoute]);
            // Fit map to route bounds
            if (allCoords.length > 0) {
                const bounds = L.latLngBounds(allCoords);
                if (bounds.isValid()) {
                    map.fitBounds(bounds, { padding: [50, 50] });
                }
            }
            console.log(`[BftRoutes] Added route ${bftRef} to map`);
        }
        catch (error) {
            console.error(`[BftRoutes] Failed to add route ${bftRef}:`, error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [viewedRoutes, api, getMap]);
    // =========================================================================
    // Remove Route
    // =========================================================================
    const removeRoute = useCallback((ourRef) => {
        const map = getMap();
        const layer = layersRef.current.get(ourRef);
        if (layer && map && map.hasLayer(layer)) {
            map.removeLayer(layer);
        }
        layersRef.current.delete(ourRef);
        setViewedRoutes(prev => prev.filter(r => r.ourRef !== ourRef));
        console.log(`[BftRoutes] Removed route ${ourRef} from map`);
    }, [getMap]);
    // =========================================================================
    // Clear All Routes
    // =========================================================================
    const clearAllRoutes = useCallback(() => {
        const map = getMap();
        layersRef.current.forEach((layer, ourRef) => {
            if (map && map.hasLayer(layer)) {
                map.removeLayer(layer);
            }
        });
        layersRef.current.clear();
        setViewedRoutes([]);
        console.log('[BftRoutes] Cleared all routes from map');
    }, [getMap]);
    // =========================================================================
    // Check if Route is Viewed
    // =========================================================================
    const isRouteViewed = useCallback((ourRef) => {
        return viewedRoutes.some(r => r.ourRef === ourRef);
    }, [viewedRoutes]);
    // =========================================================================
    // Bring layers to front periodically
    // =========================================================================
    useEffect(() => {
        const interval = setInterval(() => {
            if (isMapReady) {
                const map = getMap();
                if (map) {
                    layersRef.current.forEach(layer => {
                        if (map.hasLayer(layer)) {
                            layer.bringToFront();
                        }
                    });
                }
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [isMapReady, getMap]);
    // =========================================================================
    // Cleanup on unmount
    // =========================================================================
    useEffect(() => {
        return () => {
            const map = getMap();
            layersRef.current.forEach(layer => {
                if (map && map.hasLayer(layer)) {
                    map.removeLayer(layer);
                }
            });
            layersRef.current.clear();
        };
    }, [getMap]);
    // =========================================================================
    // Context Value
    // =========================================================================
    const value = useMemo(() => ({
        viewedRoutes,
        addRoute,
        removeRoute,
        clearAllRoutes,
        isRouteViewed,
        isLoading,
    }), [viewedRoutes, addRoute, removeRoute, clearAllRoutes, isRouteViewed, isLoading]);
    return (_jsx(BftRoutesContext.Provider, { value: value, children: children }));
};
export default BftRoutesContext;
